import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, User } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface SignatureValue {
  name: string;
  title: string;
  file_path: string;
}

interface SignatureRecord {
  id: string;
  key: string;
  value: SignatureValue;
}

const ROLE_PRESETS = [
  { key: "principal_signature", label: "Principal" },
  { key: "deputy_principal_signature", label: "Deputy Principal" },
  { key: "head_academic_signature", label: "Head of Academic" },
  { key: "head_administration_signature", label: "Head of Administration" },
  { key: "head_admission_signature", label: "Head of Admission" },
  { key: "head_examination_signature", label: "Head of Examination" },
  { key: "head_accounting_signature", label: "Head of Accounting" },
  { key: "head_student_service_signature", label: "Head of Student Services" },
  { key: "head_discipline_signature", label: "Head of Discipline" },
];

const SignatureManager = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] = useState<SignatureRecord | null>(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [customKey, setCustomKey] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  const { data: signatures, isLoading } = useQuery({
    queryKey: ["signatures"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("*")
        .like("key", "%signature%")
        .order("key");
      if (error) throw error;
      return (data || []).map((r) => ({
        id: r.id,
        key: r.key,
        value: r.value as unknown as SignatureValue,
      })) as SignatureRecord[];
    },
  });

  // Load signed URLs for all signatures
  useEffect(() => {
    if (!signatures) return;
    const loadUrls = async () => {
      const urls: Record<string, string> = {};
      for (const sig of signatures) {
        if (sig.value?.file_path) {
          const url = await getSignedFileUrl(sig.value.file_path);
          if (url) urls[sig.key] = url;
        }
      }
      setSignedUrls(urls);
    };
    loadUrls();
  }, [signatures]);

  const saveMutation = useMutation({
    mutationFn: async (params: { key: string; value: SignatureValue; existingId?: string }) => {
      const jsonValue = JSON.parse(JSON.stringify(params.value));
      if (params.existingId) {
        const { error } = await supabase
          .from("system_settings")
          .update({ value: jsonValue, updated_by: user?.id })
          .eq("id", params.existingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("system_settings")
          .insert([{ key: params.key, value: jsonValue, updated_by: user?.id }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["signatures"] });
      toast({ title: "Signature saved successfully" });
      closeDialog();
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, filePath }: { id: string; filePath?: string }) => {
      const { error } = await supabase.from("system_settings").delete().eq("id", id);
      if (error) throw error;
      if (filePath) {
        await supabase.storage.from("academic-files").remove([filePath]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["signatures"] });
      toast({ title: "Signature deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRecord(null);
    setSelectedRole("");
    setCustomKey("");
    setName("");
    setTitle("");
    setFile(null);
  };

  const openAdd = () => {
    closeDialog();
    setDialogOpen(true);
  };

  const openEdit = (record: SignatureRecord) => {
    setEditingRecord(record);
    const preset = ROLE_PRESETS.find((p) => p.key === record.key);
    setSelectedRole(preset ? record.key : "custom");
    setCustomKey(preset ? "" : record.key);
    setName(record.value.name || "");
    setTitle(record.value.title || "");
    setFile(null);
    setDialogOpen(true);
  };

  const removeWhiteBackground = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas not supported"));
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const threshold = 230; // pixels with R,G,B all above this become transparent
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] > threshold && data[i + 1] > threshold && data[i + 2] > threshold) {
            data[i + 3] = 0; // set alpha to 0
          }
        }
        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to create blob"));
        }, "image/png");
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleSave = async () => {
    const key = selectedRole === "custom" ? customKey.trim() : selectedRole;
    if (!key || !name.trim() || !title.trim()) {
      toast({ title: "Please fill all required fields", variant: "destructive" });
      return;
    }

    setUploading(true);
    let filePath = editingRecord?.value.file_path || "";

    if (file) {
      try {
        const transparentBlob = await removeWhiteBackground(file);
        const path = `signatures/${key}_${Date.now()}.png`;
        const { error } = await supabase.storage
          .from("academic-files")
          .upload(path, transparentBlob, { upsert: true, contentType: "image/png" });
        if (error) {
          toast({ title: "Upload failed", description: error.message, variant: "destructive" });
          setUploading(false);
          return;
        }
        filePath = path;
      } catch (err: any) {
        toast({ title: "Image processing failed", description: err.message, variant: "destructive" });
        setUploading(false);
        return;
      }
    }

    if (!filePath && !editingRecord) {
      toast({ title: "Please upload a signature image", variant: "destructive" });
      setUploading(false);
      return;
    }

    saveMutation.mutate({
      key,
      value: { name: name.trim(), title: title.trim(), file_path: filePath },
      existingId: editingRecord?.id,
    });
    setUploading(false);
  };

  const getLabelForKey = (key: string) => {
    const preset = ROLE_PRESETS.find((p) => p.key === key);
    return preset?.label || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const existingKeys = signatures?.map((s) => s.key) || [];
  const availablePresets = ROLE_PRESETS.filter(
    (p) => !existingKeys.includes(p.key) || editingRecord?.key === p.key
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Electronic Signatures</h2>
          <p className="text-sm text-muted-foreground">Manage official signatures used in documents</p>
        </div>
        <Button onClick={openAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Signature
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
      ) : signatures?.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <User className="h-12 w-12 mb-3 opacity-50" />
            <p>No signatures stored yet</p>
            <Button variant="outline" className="mt-3" onClick={openAdd}>
              Add First Signature
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {signatures?.map((sig) => (
            <Card key={sig.id}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{getLabelForKey(sig.key)}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(sig)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDeleteId(sig.id);
                        setDeleteKey(sig.value.file_path);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {signedUrls[sig.key] ? (
                  <div className="border rounded-md p-2 bg-muted/30 flex items-center justify-center">
                    <img
                      src={signedUrls[sig.key]}
                      alt={`${sig.value.name} signature`}
                      className="max-h-24 object-contain"
                    />
                  </div>
                ) : (
                  <div className="border rounded-md p-2 bg-muted/30 flex items-center justify-center h-24 text-muted-foreground text-sm">
                    No preview
                  </div>
                )}
                <div>
                  <p className="font-medium text-sm">{sig.value.name}</p>
                  <p className="text-xs text-muted-foreground">{sig.value.title}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRecord ? "Edit Signature" : "Add Signature"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Role / Position</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent>
                  {availablePresets.map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom...</SelectItem>
                </SelectContent>
              </Select>
              {selectedRole === "custom" && (
                <Input
                  placeholder="e.g. registrar_signature"
                  value={customKey}
                  onChange={(e) => setCustomKey(e.target.value)}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input placeholder="e.g. Dr. Glory Fela" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input placeholder="e.g. Deputy Principal" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Signature Image {editingRecord && "(leave empty to keep current)"}</Label>
              <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={uploading || saveMutation.isPending}>
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? "Uploading..." : saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => { setDeleteId(null); setDeleteKey(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Signature</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this signature record and its stored image.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) {
                  deleteMutation.mutate({ id: deleteId, filePath: deleteKey || undefined });
                  setDeleteId(null);
                  setDeleteKey(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SignatureManager;
